import {
  IHospitalConfiguration,
  IOnboardingFieldSetting,
} from "../@types/Login";
import { AUTH_HOSPITAL_CONFIGURATION } from "../constants/localStorageKeys";
import { safeJsonParse, safeStorage } from "./storage";

/**
 * The legacy per-hospital onboarding fields, as `enabledOnboardingFields` on the hospital
 * configuration still names them.
 *
 * Only the two clinical ones are left. The nine demographic codes it used to carry — Ppsn, Ihi,
 * County, NextOfKinName, NextOfKinEmail, ContactPreference, Gp, ReferralSource and Address —
 * were removed: a ward's onboarding fields are configured per ward now (EN-5192
 * `StandardOnboardingFields`), and Address is available as a predefined custom-field template.
 * A hospital config may still list the removed codes; `isOnboardingFieldEnabled` is only ever
 * asked about a code in this list, so the extras are simply never read.
 */
export const ONBOARDING_FIELD_CODES = ["Allergies", "Diagnosis"] as const;

export type OnboardingFieldCode = (typeof ONBOARDING_FIELD_CODES)[number];

export function isOnboardingFieldEnabled(
  code: OnboardingFieldCode,
  enabledFields: IOnboardingFieldSetting[] | undefined
): boolean {
  if (!enabledFields) return false;
  return enabledFields.some((field) => field.code === code);
}

export function isOnboardingFieldRequired(
  code: OnboardingFieldCode,
  enabledFields: IOnboardingFieldSetting[] | undefined
): boolean {
  if (!enabledFields) return false;
  return enabledFields.some((field) => field.code === code && field.required);
}

// Tolerates the legacy cached shape (a plain array of code strings) that may still
// be in localStorage for users who logged in before required flags existed. Such
// fields are treated as enabled-but-optional until the next login refreshes the config.
function normalizeOnboardingFields(fields: unknown): IOnboardingFieldSetting[] {
  if (!Array.isArray(fields)) return [];
  return fields.map((field) =>
    typeof field === "string"
      ? { code: field, required: false }
      : (field as IOnboardingFieldSetting)
  );
}

export function getEnabledOnboardingFields(): IOnboardingFieldSetting[] {
  const raw = safeStorage.getItem(AUTH_HOSPITAL_CONFIGURATION);
  const parsed = safeJsonParse<IHospitalConfiguration | undefined>(
    raw,
    undefined
  );
  return normalizeOnboardingFields(parsed?.enabledOnboardingFields);
}
